import { PERFIS, atualizarUsuarioSchema, criarUsuarioSchema } from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import {
  BarraTitulo,
  BotaoEditar,
  Formulario,
  RodapeGaveta,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { Campo, Checkbox, Input, Select } from '@/components/ui/campo'
import { Gaveta } from '@/components/ui/gaveta'
import { Ativo, Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { formatarData, paraInputData } from '@/lib/datas'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/usuarios')({ component: Usuarios })

const ROTULO_PERFIL: Record<(typeof PERFIS)[number], string> = {
  DIRETORIA: 'Diretoria',
  GESTAO: 'Gestão / PCP',
  OPERADOR: 'Operador',
}

type Item = NonNullable<ReturnType<typeof useUsuarios>['data']>[number]
function useUsuarios() {
  const trpc = useTRPC()
  return useQuery(trpc.cadastros.usuarios.listar.queryOptions())
}

function Usuarios() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useUsuarios()
  const turnos = useQuery(trpc.cadastros.turnos.listar.queryOptions())
  const g = useGavetaCadastro<Item>()
  const [perfil, setPerfil] = useState<(typeof PERFIS)[number]>('OPERADOR')
  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.cadastros.usuarios.listar.queryKey() })
  const criar = useMutation(
    trpc.cadastros.usuarios.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const atualizar = useMutation(
    trpc.cadastros.usuarios.atualizar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )

  const abrirNovo = () => {
    setPerfil('OPERADOR')
    g.abrirNovo()
  }
  const abrirEditar = (u: Item) => {
    setPerfil(u.perfil)
    g.abrirEditar(u)
  }

  // Senha e PIN são `optional()` no schema: vazio some do objeto (não vira null).
  const comum = {
    opcionais: ['email', 'admitidoEm', 'turnoId'],
    indefinidos: ['senha', 'pin'],
    booleanos: ['ativo'],
    setErros: g.setErros,
  }
  const submitCriar = aoSubmeter({
    ...comum,
    schema: criarUsuarioSchema,
    sucesso: (d) => `${d.nome} cadastrado.`,
    onValido: (d) => criar.mutateAsync(limparVazios(d)),
  })
  const submitEditar = aoSubmeter({
    ...comum,
    schema: atualizarUsuarioSchema,
    extra: { id: g.item?.id },
    sucesso: (d) => `${d.nome} salvo.`,
    onValido: (d) => atualizar.mutateAsync(limparVazios(d)),
  })

  const operador = perfil === 'OPERADOR'

  return (
    <>
      <BarraTitulo
        titulo="Usuários"
        descricao="Operadores entram no tablet com PIN; gestão e diretoria entram pela web com e-mail e senha."
        onNovo={abrirNovo}
        rotuloNovo="Novo usuário"
      />
      <Tabela>
        <Cabecalho>
          <Th>Nome</Th>
          <Th>Perfil</Th>
          <Th>E-mail</Th>
          <Th>Turno</Th>
          <Th>Admissão</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((u) => (
            <Linha key={u.id}>
              <Td className="font-medium">
                {u.nome} {!u.ativo && <Ativo ativo={false} />}
              </Td>
              <Td className="text-fg-muted">{ROTULO_PERFIL[u.perfil]}</Td>
              <Td className="text-fg-muted">{u.email ?? '—'}</Td>
              <Td className="text-fg-muted">{u.turno?.nome ?? '—'}</Td>
              <Td numerico>{formatarData(u.admitidoEm)}</Td>
              <Td className="text-right">
                <BotaoEditar rotulo={u.nome} onClick={() => abrirEditar(u)} />
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={6} />}
          {lista.data?.length === 0 && <Vazio colunas={6}>Nenhum usuário.</Vazio>}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar usuário' : 'Novo usuário'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-usuario"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario
          id="form-usuario"
          onSubmit={g.item ? submitEditar : submitCriar}
          erros={g.erros}
          onLimparErro={g.limparErro}
        >
          <Campo label="Nome" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} autoFocus />}
          </Campo>
          <Campo label="Perfil" erro={g.erros.perfil}>
            {(p) => (
              <Select
                {...p}
                name="perfil"
                value={perfil}
                onChange={(ev) => setPerfil(ev.target.value as (typeof PERFIS)[number])}
              >
                {PERFIS.map((pf) => (
                  <option key={pf} value={pf}>
                    {ROTULO_PERFIL[pf]}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          {operador ? (
            <Campo
              label={g.item ? 'Novo PIN (deixe vazio para manter)' : 'PIN do tablet'}
              erro={g.erros.pin}
              dica="4 a 6 dígitos. Só o operador deve conhecer."
            >
              {(p) => (
                <Input {...p} name="pin" inputMode="numeric" autoComplete="off" maxLength={6} />
              )}
            </Campo>
          ) : (
            <>
              <Campo label="E-mail" erro={g.erros.email}>
                {(p) => (
                  <Input {...p} name="email" type="email" defaultValue={g.item?.email ?? ''} />
                )}
              </Campo>
              <Campo
                label={g.item ? 'Nova senha (deixe vazio para manter)' : 'Senha inicial'}
                erro={g.erros.senha}
              >
                {(p) => <Input {...p} name="senha" type="password" autoComplete="new-password" />}
              </Campo>
            </>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo label="Admissão" erro={g.erros.admitidoEm} dica="Base da curva dos 60 dias.">
              {(p) => (
                <Input
                  {...p}
                  name="admitidoEm"
                  type="date"
                  defaultValue={paraInputData(g.item?.admitidoEm)}
                />
              )}
            </Campo>
            <Campo label="Turno" erro={g.erros.turnoId}>
              {(p) => (
                <Select {...p} name="turnoId" defaultValue={g.item?.turnoId ?? ''}>
                  <option value="">Sem turno</option>
                  {turnos.data?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>
          </div>
          <Checkbox name="ativo" label="Ativo" defaultChecked={g.item?.ativo ?? true} />
        </Formulario>
      </Gaveta>
    </>
  )
}

/** Senha/PIN vazios (campo deixado em branco na edição) não devem ir para a API. */
function limparVazios<T extends { senha?: string | undefined; pin?: string | undefined }>(d: T): T {
  const copia = { ...d }
  if (!copia.senha) delete copia.senha
  if (!copia.pin) delete copia.pin
  return copia
}
